import { FREE_SHIPPING_OPTION } from "@/domains/careplan/models/constants";
import type { CareplanOrderModel } from "@/domains/careplan/models/types";
import { Divider, Heading } from "@/ui";
import { formatAmount } from "@/utils/money";
import { CareplanCartLineItem } from "./careplan-cart-line-item";

type CareplanCartSubscriptionSummaryCardProps = {
  order: CareplanOrderModel;
};

export const CareplanCartSubscriptionSummaryCard = ({
  order,
}: CareplanCartSubscriptionSummaryCardProps) => {
  if (order.isEmpty) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 rounded-xl bg-canvas p-5 text-sm">
      <Heading level={4}>Subscription Summary</Heading>

      {order.activeLines.map((line) => {
        return (
          <CareplanCartLineItem key={line.id}>
            <p className="text-content-secondary">{`${line.shortName} x ${line.quantity}`}</p>
            <p className="font-medium">${formatAmount(line.unitListPrice * line.quantity)}</p>
          </CareplanCartLineItem>
        );
      })}

      <Divider.Line />

      <CareplanCartLineItem>
        <p className="text-content-secondary">Subtotal</p>
        <p className="font-medium">${order.subtotalAsString}</p>
      </CareplanCartLineItem>

      <div className="flex flex-col gap-1">
        <CareplanCartLineItem className="text-danger">
          <p>Care plan savings</p>
          <p className="font-medium">- ${order.savingsAsString}</p>
        </CareplanCartLineItem>
        <p className="text-content-subtle">
          {order.hasCareplanCode
            ? "$5 off each product + 10% for subscribing"
            : "10% for subscribing"}
        </p>
      </div>

      {order.extraDiscounts.map((discount) => {
        return (
          <CareplanCartLineItem className="text-danger" key={discount.code}>
            <p>{discount.code}</p>
            <p className="font-medium">- ${discount.displayTotal}</p>
          </CareplanCartLineItem>
        );
      })}

      {order.shipping ? (
        <CareplanCartLineItem>
          <p>{order.shipping.title}</p>
          <p className="font-medium">
            {order.shipping.costAsString === FREE_SHIPPING_OPTION
              ? FREE_SHIPPING_OPTION
              : `$${order.shipping.costAsString}`}
          </p>
        </CareplanCartLineItem>
      ) : null}

      <Divider.Line />

      <CareplanCartLineItem className="items-baseline">
        <p>
          <span className="text-lg font-bold">Total</span>
          <span className="ml-1.5 text-content-secondary">per month</span>
        </p>
        <p className="text-lg font-bold">${order.totalAsString}</p>
      </CareplanCartLineItem>
    </div>
  );
};
